import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Code2, KeyRound, RefreshCw, Trash2, Upload, Download, Copy, CheckCircle2, FileSpreadsheet, Terminal } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function MerchantDevApi({ company }) {
  const [keyInfo, setKeyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadKey = () => {
    setLoading(true);
    api.get(`/integrations/api-key/${company.id}`).then((r) => setKeyInfo(r.data)).catch(() => setKeyInfo({ api_key: null })).finally(() => setLoading(false));
  };
  useEffect(() => { if (company?.id) loadKey(); }, [company?.id]);

  const generate = async () => {
    if (keyInfo?.api_key && !window.confirm("Já existe uma chave. Gerar uma nova VAI invalidar a atual. Continuar?")) return;
    setBusy(true);
    try { const { data } = await api.post(`/integrations/api-key/${company.id}/generate`); setKeyInfo(data); toast.success("Chave gerada"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revogar a chave atual? Integrações que usam essa chave vão parar de funcionar.")) return;
    try { await api.delete(`/integrations/api-key/${company.id}`); setKeyInfo({ api_key: null }); toast.success("Chave revogada"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyKey = () => {
    if (!keyInfo?.api_key) return;
    navigator.clipboard.writeText(keyInfo.api_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/integrations/csv-template");
    const blob = new Blob([data.content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = data.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/integrations/csv-import/${company.id}/products`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.inserted} produto(s) importado(s)${data.errors?.length ? ` · ${data.errors.length} erro(s)` : ""}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha na importação"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const apiBase = `${BACKEND}/api/integrations`;
  const curlCreate = `curl -X POST "${apiBase}/products" \\
  -H "X-API-Key: ${keyInfo?.api_key || "<sua-chave>"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Pizza Margherita",
    "description": "Mussarela e manjericão",
    "kind": "product",
    "category": "Pizzas",
    "price": 49.90,
    "image_url": "https://exemplo.com/pizza.jpg",
    "sale_url": "https://wa.me/5511999999999",
    "active": true,
    "checkout_enabled": true,
    "cashback_pct_on_checkout": 8
  }'`;
  const curlBulk = `curl -X POST "${apiBase}/products/bulk" \\
  -H "X-API-Key: ${keyInfo?.api_key || "<sua-chave>"}" \\
  -H "Content-Type: application/json" \\
  -d '[
    {"title":"Pizza Margherita","price":49.90,"image_url":"https://exemplo.com/pizza.jpg","checkout_enabled":true,"cashback_pct_on_checkout":8},
    {"title":"Coca-Cola 2L","price":12.50,"category":"Bebidas","image_url":"https://exemplo.com/coca.jpg"}
  ]'`;
  const curlHealth = `curl "${apiBase}/health" -H "X-API-Key: ${keyInfo?.api_key || "<sua-chave>"}"`;

  return (
    <div className="space-y-5">
      <Card className="p-5 rounded-2xl border-stone-200">
        <div className="flex items-center gap-2 mb-2">
          <Code2 size={20} className="text-blue-900"/>
          <h3 className="font-display text-xl font-semibold">Área Dev — Integrações de {company.name}</h3>
        </div>
        <p className="text-sm text-stone-500">Conecte seu PDV, ERP ou planilha. Cadastre produtos via API ou via CSV.</p>
      </Card>

      {/* API KEY */}
      <Card className="p-5 rounded-2xl border-stone-200" data-testid="dev-key-card">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={18} className="text-amber-600"/>
          <h4 className="font-display text-lg font-semibold">Chave de API</h4>
        </div>
        {loading ? <p className="text-sm text-stone-500">Carregando...</p> : keyInfo?.api_key ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={keyInfo.api_key} readOnly className="rounded-xl font-mono text-xs flex-1 min-w-[300px] bg-stone-50" data-testid="dev-key-value"/>
              <Button onClick={copyKey} variant="outline" className="rounded-xl" data-testid="dev-key-copy">
                {copied ? <><CheckCircle2 size={14} className="mr-1 text-emerald-600"/>Copiado</> : <><Copy size={14} className="mr-1"/>Copiar</>}
              </Button>
              <Button onClick={generate} disabled={busy} variant="outline" className="rounded-xl" data-testid="dev-key-regen"><RefreshCw size={14} className="mr-1"/>Regerar</Button>
              <Button onClick={revoke} variant="ghost" className="rounded-xl text-red-600 hover:bg-red-50" data-testid="dev-key-revoke"><Trash2 size={14} className="mr-1"/>Revogar</Button>
            </div>
            <p className="text-[11px] text-amber-700 mt-2">⚠️ Esta chave dá acesso aos seus produtos. Não compartilhe publicamente. Mantenha em variáveis de ambiente.</p>
          </>
        ) : (
          <Button onClick={generate} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="dev-key-generate">
            <KeyRound size={14} className="mr-1.5"/>Gerar minha chave de API
          </Button>
        )}
      </Card>

      {/* CSV */}
      <Card className="p-5 rounded-2xl border-stone-200" data-testid="dev-csv-card">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet size={18} className="text-emerald-600"/>
          <h4 className="font-display text-lg font-semibold">Importação por CSV</h4>
        </div>
        <p className="text-sm text-stone-600 mb-3">Baixe o modelo, preencha em Excel/Google Sheets e envie.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button onClick={downloadTemplate} variant="outline" className="rounded-xl" data-testid="dev-csv-template"><Download size={14} className="mr-1.5"/>Baixar modelo CSV</Button>
          <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold cursor-pointer">
            <Upload size={14}/> Enviar CSV
            <input type="file" accept=".csv,text/csv" hidden onChange={uploadCsv} disabled={busy} data-testid="dev-csv-upload"/>
          </label>
        </div>
        <details className="text-xs text-stone-600">
          <summary className="cursor-pointer hover:text-stone-900">Quais colunas usar?</summary>
          <ul className="mt-2 ml-4 list-disc space-y-1">
            <li><code>title</code> ou <code>titulo</code> (obrigatório)</li>
            <li><code>description</code> ou <code>descricao</code></li>
            <li><code>kind</code> ou <code>tipo</code> (<code>product</code> ou <code>service</code>)</li>
            <li><code>category</code> ou <code>categoria</code></li>
            <li><code>price</code> ou <code>preco</code> (use ponto ou vírgula como separador decimal)</li>
            <li><code>image_url</code>, <code>sale_url</code></li>
            <li><code>active</code>, <code>checkout_enabled</code> (true/false)</li>
            <li><code>cashback_pct_on_checkout</code> (0–100)</li>
          </ul>
        </details>
        {importResult && (
          <div className="mt-3 p-3 rounded-xl bg-stone-50 border border-stone-200 text-xs space-y-1">
            <p><strong>{importResult.inserted}</strong> linha(s) importada(s).</p>
            {importResult.errors?.length > 0 && (
              <details>
                <summary className="text-red-600 cursor-pointer">{importResult.errors.length} erro(s)</summary>
                <ul className="ml-4 mt-1 list-disc text-red-700">
                  {importResult.errors.map((e, i) => <li key={i}>Linha {e.line}: {e.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </Card>

      {/* EXAMPLES */}
      <Card className="p-5 rounded-2xl border-stone-200">
        <div className="flex items-center gap-2 mb-3">
          <Terminal size={18} className="text-stone-700"/>
          <h4 className="font-display text-lg font-semibold">Exemplos de uso da API</h4>
        </div>
        <div className="space-y-3 text-xs">
          <div>
            <p className="font-semibold text-stone-700 mb-1">1. Verificar a chave</p>
            <pre className="bg-stone-900 text-stone-100 p-3 rounded-xl overflow-x-auto whitespace-pre-wrap break-all">{curlHealth}</pre>
          </div>
          <div>
            <p className="font-semibold text-stone-700 mb-1">2. Criar um produto</p>
            <pre className="bg-stone-900 text-stone-100 p-3 rounded-xl overflow-x-auto whitespace-pre-wrap break-all">{curlCreate}</pre>
          </div>
          <div>
            <p className="font-semibold text-stone-700 mb-1">3. Criar vários de uma vez</p>
            <pre className="bg-stone-900 text-stone-100 p-3 rounded-xl overflow-x-auto whitespace-pre-wrap break-all">{curlBulk}</pre>
          </div>
        </div>
        <p className="text-[11px] text-stone-500 mt-3">Base URL: <code className="bg-stone-100 px-1 py-0.5 rounded">{apiBase}</code> · Header: <code className="bg-stone-100 px-1 py-0.5 rounded">X-API-Key</code></p>
        <details className="mt-2 text-xs text-stone-600">
          <summary className="cursor-pointer hover:text-stone-900">Todos os campos aceitos pelo produto</summary>
          <table className="mt-2 w-full text-[11px] border border-stone-200 rounded-lg overflow-hidden">
            <thead className="bg-stone-50"><tr><th className="text-left p-1.5">Campo</th><th className="text-left p-1.5">Tipo</th><th className="text-left p-1.5">Descrição</th></tr></thead>
            <tbody>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>title</code> *</td><td>string</td><td>Nome do produto/serviço</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>description</code></td><td>string</td><td>Descrição detalhada</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>kind</code></td><td>string</td><td><code>product</code> ou <code>service</code> (default: product)</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>category</code></td><td>string</td><td>Categoria (use uma já cadastrada para aparecer no filtro)</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>price</code></td><td>float</td><td>Preço em R$ (use ponto como decimal)</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5 text-amber-700"><code>image_url</code></td><td>string</td><td>URL pública da imagem (jpg/png/webp)</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>sale_url</code></td><td>string</td><td>Link externo (loja/whatsapp) — opcional</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>active</code></td><td>bool</td><td>Visível no catálogo (default: true)</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>checkout_enabled</code></td><td>bool</td><td>Vender pelo Checkout MorarBem (default: false)</td></tr>
              <tr className="border-t border-stone-100"><td className="p-1.5"><code>cashback_pct_on_checkout</code></td><td>float</td><td>% de BemCash a devolver (0–100)</td></tr>
            </tbody>
          </table>
        </details>
      </Card>
    </div>
  );
}
